import React, { useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

/** Util: convierte hex → rgba con alpha opcional */
function toRGBA(hex: string, a = 0.7) {
  const h = hex.replace("#", "").trim();
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${a})`;
}

/** Devuelve número de días del mes (1..n) */
function daysInMonth(year: number, monthIndex0: number) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function ultimoDiaDelMes(year: number, monthIndex0: number): number {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}
/** Construye un mapa día→valor y densifica 1..toDay con 0 si falta. */
function densifyByDay(
  datos: Array<any>,
  metricKey: string,
  year: number,
  monthIndex0: number,
  toDay?: number,
  anioMesSeleccionado?:string
) {
  const [anio,mes] = anioMesSeleccionado.split("_")
  const dim = daysInMonth(year, monthIndex0);
  const now = new Date();
  const isCurrentMonth =
    now.getFullYear() === year && now.getMonth() === monthIndex0;
  const limitDay = ultimoDiaDelMes(Number(anio),Number(mes)-1)

  // CASO ESPECIAL: Si hay algún registro con dia_leido_del_mes = 0, significa que NO HAY LECTURAS
  const hasZeroDay = datos?.some(d => Number(d?.dia_leido_del_mes) === 0);
  
  if (hasZeroDay) {
    // Mostrar todos los días del mes hasta hoy con valor 0
    const labels: string[] = [];
    const values: number[] = [];
    const weekendIdx: number[] = [];

    for (let day = 1; day <= limitDay; day++) {
      const date = new Date(year, monthIndex0, day);
      const label = date
        .toLocaleDateString("es-ES", {
          weekday: "short",
          day: "numeric",
          month: "short",
        })
        .replace(",", "");
      labels.push(label);
      values.push(0); // Todos los días en 0

      const dow = date.getDay();
      if (dow === 0 || dow === 6) weekendIdx.push(day - 1);
    }

    return { labels, values, weekendIdx, isEmptyMonth: true };
  }

  // CASO NORMAL: Procesar datos normalmente
  const map: Record<number, number> = {};
  for (const d of datos || []) {
    const raw = Number(d?.[metricKey] ?? 0);
    const dia = Number(d?.dia ?? d?.dia_leido_del_mes ?? 0);
    if (dia >= 1 && dia <= dim) {
      map[dia] = raw;
    }
  }

  const labels: string[] = [];
  const values: number[] = [];
  const weekendIdx: number[] = [];

  for (let day = 1; day <= limitDay; day++) {
    const date = new Date(year, monthIndex0, day);
    const label = date
      .toLocaleDateString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
      .replace(",", "");
    labels.push(label);
    values.push(map[day] ?? 0); // 0 para días sin datos

    const dow = date.getDay();
    if (dow === 0 || dow === 6) weekendIdx.push(day - 1);
  }

  return { labels, values, weekendIdx, isEmptyMonth: false };
}

/** Plugin para sombrear fines de semana en eje X categórico */
const weekendPlugin = {
  id: "weekendBackground",
  beforeDatasetsDraw(chart: any) {
    const { ctx, chartArea, scales } = chart;
    const indexes: number[] = chart.options.weekendIndexes || [];
    if (!chartArea || !indexes?.length) return;

    ctx.save();
    const xScale = scales.x;

    indexes.forEach((i) => {
      const left = xScale.getPixelForValue(i - 0.5);
      const right = xScale.getPixelForValue(i + 0.5);
      const width = right - left;

      ctx.fillStyle = "rgba(99, 102, 241, 0.08)";
      ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);

      ctx.strokeStyle = "rgba(99, 102, 241, 0.15)";
      ctx.lineWidth = 1;
      ctx.strokeRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
    });

    ctx.restore();
  },
};

type MetricChartProps = {
  datosProcesados: Array<any>;
  metricFilter: string;
  metricas: Record<
    string,
    { label: string; colorHex?: string; format?: (v: number) => string }
  >;
  viewType?: "bar" | "line";
  maxValor?: number;
  /** Opcional: fija año/mes a graficar; por defecto usa el mes y año actuales */
  yearMonth?: { year: number; monthIndex0: number; toDay?: number };
  /** Indica si se seleccionó un colegio específico */
  isColegioSelected?: boolean;
  anioMesSeleccionado?:string;
};

export default function MetricChart({
  datosProcesados,
  metricFilter,
  metricas,
  viewType = "bar",
  maxValor,
  yearMonth,
  isColegioSelected = false,
  anioMesSeleccionado,
}: MetricChartProps) {
  const [year,month] = anioMesSeleccionado.split("_")
  const chartRef = useRef(null);
  const today = new Date();
  const targetYear = Number(year)
  const targetMonth0 = Number(month)-1
  const toDayLimit = yearMonth?.toDay;

  const {
    labels,
    values,
    weekendIdx,
    isEmptyMonth,
    metainfo: { colorHex, label, formatter, maxY },
  } = useMemo(() => {
    // 1) Densificar días 1..hoy/fin de mes
    const { labels, values, weekendIdx, isEmptyMonth } = densifyByDay(
      datosProcesados,
      metricFilter,
      targetYear,
      targetMonth0,
      toDayLimit,
      anioMesSeleccionado
    );

    // 2) Rango Y
    const vmax = Math.max(0, ...(values.length ? values : [0]));
    const isPercentScale =
      metricFilter.toLowerCase().includes("igl") ||
      metricFilter.toLowerCase().includes("icp") ||
      metricFilter.toLowerCase().includes("norm");

    // CONVERTIR VALORES A PORCENTAJE SI CORRESPONDE
    const convertedValues = values.map(v => {
      if (isPercentScale) {
        return v * 100; // Convertir 0-1 a 0-100
      }
      return v;
    });

    const computedMaxY =
      typeof maxValor === "number"
        ? maxValor
        : isPercentScale
        ? 100 // Ahora máximo 100% en lugar de 1
        : Math.ceil(vmax * 1.15);

    // 3) Meta de la métrica
    const met = metricas?.[metricFilter] || {};
    const baseColorHex = isEmptyMonth ? "#9CA3AF" : (met.colorHex || "#111827");
    const label = met.label || metricFilter;
    
    // Formateador mejorado para porcentajes
    const formatter =
      typeof met.format === "function"
        ? met.format
        : (v: number) => {
            if (isPercentScale) {
              return `${v.toFixed(1)}%`;
            }
            return Number.isInteger(v) ? v.toString() : v.toFixed(1);
          };

    return {
      labels,
      values: convertedValues,
      weekendIdx,
      isEmptyMonth,
      metainfo: { 
        colorHex: baseColorHex, 
        label, 
        formatter, 
        maxY: computedMaxY 
      },
    };
  }, [
    datosProcesados,
    metricFilter,
    metricas,
    maxValor,
    targetYear,
    targetMonth0,
    toDayLimit,
  ]);

  const commonOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: isEmptyMonth ? "#6B7280" : "#1F2937",
          titleColor: "#F9FAFB",
          bodyColor: "#F9FAFB",
          borderColor: isEmptyMonth ? "rgba(156,163,175,0.2)" : "rgba(99,102,241,0.2)",
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          cornerRadius: 8,
          callbacks: {
            title: (items) => {
              const i = items?.[0]?.dataIndex ?? 0;
              const date = new Date(targetYear, targetMonth0, i + 1);
              const dayName = date.toLocaleDateString("es-ES", { weekday: "long" });
              const isWeekend = weekendIdx.includes(i);
              const weekendIcon = isWeekend ? " 🏖️" : "";
              return `${dayName} ${date.getDate()} de ${date.toLocaleDateString("es-ES", { month: "long" })}${weekendIcon}`;
            },
            label: (ctx) => {
              const value = Number(ctx.parsed.y ?? 0);
              if (isEmptyMonth) return `${label}: Sin lecturas este día`;
              const show = Number.isFinite(value) ? (maxY === 100 ? value.toFixed(1) + "%" : (Number.isInteger(value) ? value : value.toFixed(1))) : "0";
              const hasValue = value > 0;
              const icon = hasValue ? "📖" : "⏸️";
              return `${label}: ${show} ${icon}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: isEmptyMonth ? "rgba(156,163,175,0.06)" : "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: {
            autoSkip: false,
            maxRotation: 45,   // ← inclinado para ver todo el mes
            minRotation: 45,
            color: (ctx) => (isEmptyMonth ? "#9CA3AF" : weekendIdx.includes(ctx.index) ? "#6366F1" : "#6B7280"),
            font: { size: 11 },
          },
        },
        y: {
          min: 0,
          max: maxY,
          grid: { color: maxY === 100 ? "rgba(15,23,42,0.12)" : "rgba(15,23,42,0.06)", drawTicks: false },
          ticks: {
            color: isEmptyMonth ? "#9CA3AF" : "#6B7280",
            font: { size: 11 },
            callback: (v) => (maxY === 100 ? `${v}%` : v),
          },
          border: { display: false },
        },
      },
      weekendIndexes: weekendIdx,
    }),
    [label, maxY, weekendIdx, targetYear, targetMonth0, isEmptyMonth]
  );
  const buildGradient = (ctx: any, baseColor: string) => {
    const { chartArea, ctx: c } = ctx.chart;
    if (!chartArea) return baseColor;
    const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    
    if (isEmptyMonth) {
      g.addColorStop(0, toRGBA("#9CA3AF", 0.3));
      g.addColorStop(1, toRGBA("#9CA3AF", 0.1));
    } else {
      g.addColorStop(0, toRGBA(baseColor, viewType === "line" ? 0.4 : 0.7));
      g.addColorStop(1, toRGBA(baseColor, viewType === "line" ? 0.1 : 0.15));
    }
    
    return g;
  };

  const dataBar = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label,
        data: values,
        backgroundColor: (ctx: any) => buildGradient(ctx, colorHex),
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 24,
        hoverBackgroundColor: isEmptyMonth ? toRGBA("#9CA3AF", 0.5) : toRGBA(colorHex, 0.9),
        categoryPercentage: 0.8,
        barPercentage: 0.9,
      },
    ],
  };

  const dataLine = {
    labels,
    datasets: [
      {
        type: "line" as const,
        label,
        data: values,
        fill: true,
        tension: 0.4,
        borderWidth: isEmptyMonth ? 2 : 3,
        borderColor: colorHex,
        borderDash: isEmptyMonth ? [5, 5] : [],
        backgroundColor: (ctx: any) => buildGradient(ctx, colorHex),
        pointRadius: (ctx: any) => {
          if (isEmptyMonth) return 0;
          return weekendIdx.includes(ctx.dataIndex) ? 5 : 3;
        },
        pointBackgroundColor: (ctx: any) => {
          if (isEmptyMonth) return "#9CA3AF";
          return weekendIdx.includes(ctx.dataIndex) ? "#6366F1" : colorHex;
        },
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        pointHoverRadius: 8,
        pointHitRadius: 12,
      },
    ],
  };

  // Mensaje especial para mes sin lecturas
  if (isEmptyMonth && isColegioSelected) {
    return (
      <div className="h-64 sm:h-72 md:h-80 lg:h-96 rounded-xl border border-gray-300 bg-gray-50 p-6 flex flex-col items-center justify-center text-center">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-2">Sin lecturas este mes</h3>
        <p className="text-gray-500 text-sm max-w-md">
          El colegio seleccionado no ha registrado lecturas durante este mes hasta la fecha.
        </p>
      </div>
    );
  }

  return (
    <div className={`h-64 sm:h-72 md:h-80 lg:h-96 rounded-xl border ${
      isEmptyMonth ? 'border-gray-300 bg-gray-50' : 'border-gray-200 bg-white'
    } p-4 shadow-sm`}>
      {viewType === "line" ? (
        <Line ref={chartRef} data={dataLine} options={commonOptions as any} plugins={[weekendPlugin]} />
      ) : (
        <Bar ref={chartRef} data={dataBar} options={commonOptions as any} plugins={[weekendPlugin]} />
      )}
    </div>
  );
}